import type { CategorySearchOptions, ProductSearchOptions } from "@bloomreach/discovery-web-sdk";
import type { FilterState } from "~/lib/filters/types";
import type { PlpQueryParams, SearchQueryParams } from "./types";
import { BLOOMREACH_DEFAULT_FL, BLOOMREACH_DEFAULT_ROWS } from "./config";
import { readBrUid, PLACEHOLDER_BR_UID } from "./read-br-uid";

// content_injection is not typed in the SDK but is passed through verbatim in the request.
export type CategorySearchOptionsExt = CategorySearchOptions & {
  content_injection?: "true" | "false";
};

const BLOOMREACH_CORE_ENDPOINT = "https://core.dxpapi.com/api/v1/core/";

// _br_uid_2 is pre-encoded by the caller; the SDK passes it through verbatim.
function serializeBloomreachParams(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined) continue;
    const passthrough = key === "_br_uid_2";
    if (Array.isArray(val)) {
      for (const item of val) {
        parts.push(`${key}=${passthrough ? String(item) : encodeURIComponent(String(item))}`);
      }
    } else {
      parts.push(`${key}=${passthrough ? String(val) : encodeURIComponent(String(val))}`);
    }
  }
  return parts.join("&");
}

// SDK defaults that options override — kept separate to avoid duplicate-key warnings.
const CATEGORY_SEARCH_DEFAULTS = { fl: "pid", start: 0, "facet.version": "3.0" } as const;
// Fixed params appended last — cannot be overridden by options.
const CATEGORY_SEARCH_FIXED = { request_type: "search", search_type: "category" } as const;

/**
 * Builds the URL that the Bloomreach SDK's categorySearch() would fetch.
 * Replicates the SDK merge order: q → config → SDK defaults → options → fixed params.
 * Serialises options as-is — the real _br_uid_2 must already be in options (injected by
 * buildBaseSearchOptions via readBrUid). This function does not call readBrUid itself.
 *
 * For the prefetch/adoption key-match comparison, use buildBloomreachCacheKey instead.
 */
export function buildBloomreachSearchUrl(
  config: { accountId: string; domainKey: string },
  collectionKey: string,
  options: CategorySearchOptionsExt,
): string {
  const params: Record<string, unknown> = {
    q: collectionKey,
    account_id: Number(config.accountId),
    domain_key: config.domainKey,
    ...CATEGORY_SEARCH_DEFAULTS,
    ...options,
    ...CATEGORY_SEARCH_FIXED,
  };
  return `${BLOOMREACH_CORE_ENDPOINT}?${serializeBloomreachParams(params)}`;
}

/**
 * Builds a stable cache key for the WEB-990 prefetch/adopt key-match comparison.
 *
 * Normalises `_br_uid_2` to the documented placeholder regardless of when the real
 * BrTrk cookie was written. Both plp-prefetch.ts (module-eval time, cookie may be
 * absent) and create-plp-listing.svelte.ts (queryFn time, cookie may now be present)
 * must use this function for the comparison so the keys always match.
 *
 * The actual fetch URL (plp-prefetch.ts) and the SDK call (create-plp-listing.svelte.ts)
 * still use buildBloomreachSearchUrl / the SDK directly — those carry the real cookie.
 */
export function buildBloomreachCacheKey(
  config: { accountId: string; domainKey: string },
  collectionKey: string,
  options: CategorySearchOptionsExt,
): string {
  return buildBloomreachSearchUrl(config, collectionKey, {
    ...options,
    _br_uid_2: PLACEHOLDER_BR_UID,
  });
}

type CommonParams = {
  currentUrl: string;
  rows?: number;
  start?: number;
  refUrl?: string;
  activeFilters?: FilterState;
  sort?: string;
};

function buildBaseSearchOptions(params: CommonParams): ProductSearchOptions {
  const opts: ProductSearchOptions = {
    fl: BLOOMREACH_DEFAULT_FL.join(","),
    url: params.currentUrl,
    rows: params.rows ?? BLOOMREACH_DEFAULT_ROWS,
    start: params.start ?? 0,
    _br_uid_2: readBrUid(),
    "facet.version": "3.0",
    "stats.field": "price",
  };
  if (params.refUrl) opts.ref_url = params.refUrl;
  if (params.sort) opts.sort = params.sort;
  if (params.activeFilters) {
    const fq = buildFqParams(params.activeFilters);
    if (fq.length > 0) opts.fq = fq;
  }
  return opts;
}

export function buildCategorySearchOptions(params: PlpQueryParams): CategorySearchOptionsExt {
  return { ...buildBaseSearchOptions(params), content_injection: "true" };
}

export function buildProductSearchOptions(params: SearchQueryParams): ProductSearchOptions {
  return buildBaseSearchOptions(params);
}

// Note: field names from the API can contain spaces and special characters
// (e.g. "With Storage?", "Number Of Seats"). The fq param passes the name verbatim —
// the Bloomreach API accepts it. No quoting of the field name itself is required.
export function buildFqParams(filterValues: FilterState): string[] {
  const params: string[] = [];

  for (const [fieldId, value] of Object.entries(filterValues)) {
    if (!Array.isArray(value) || value.length === 0) continue;

    if (typeof value[0] === "number" && typeof value[1] === "number") {
      // SliderFilter: [min, max] → field:[min TO max]
      params.push(`${fieldId}:[${value[0]} TO ${value[1]}]`);
    } else if (typeof value[0] === "string") {
      // CheckboxFilter: string[] → field:("val1" OR "val2")
      const quoted = (value as string[]).map((v) => `"${v}"`).join(" OR ");
      params.push(`${fieldId}:(${quoted})`);
    }
  }

  return params;
}
